import { useEffect, useMemo, useRef } from 'react';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/components/ui';
import { embedLayout } from '@/lib/embed-layout';
import { useProductSelectionStore } from '@/stores';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { useDesignStore } from '@/stores/designStore';
import { hasGuidedWork, useGuidedEntryStore } from '@/stores/guidedEntryStore';
import { useProducts } from '@/hooks/useProducts';
import { logger } from '@/utils/logger';
import {
  FRONT_PRESETS,
  BACK_PRESETS,
  DEFAULT_MAX_THREAD_COLORS,
  MAX_EMBROIDERY_WIDTH_MM,
  createPrintContext,
} from '@designdeck/shared';
import {
  CUSTOM_PLACEMENT,
  type GuidedProcedure,
  type GuidedSide,
} from '@/stores/guidedEntryStore';
import {
  GuidedMotifCard,
  type GuidedEmbroideryLimits,
} from './GuidedMotifCard';
import { AdvancedEntryLink } from './AdvancedEntryLink';
import {
  guidedFrameContext,
  guidedPrintContextParams,
} from './guidedCanvasConfig';
import {
  planGuidedCommit,
  guidedCommitSkipMessage,
  printContextForPosition,
} from './guidedCommit';
import type { PlacementOption } from './GuidedPlacementPicker';
import {
  otherProcedure,
  productSupportsProcedure,
} from './guidedProductNotice';
import {
  CHOICE_TRANSITION,
  GuidedProcedureChoice,
} from './GuidedProcedureChoice';
import { useGuidedStartProcedure } from './useGuidedStartProcedure';
import { useProcedureSwitch } from './useProcedureSwitch';

/**
 * Step 1 of the guided creator entry: the motif comes before the product.
 *
 * A full-width scrolling page rather than the three-column editor shell — the
 * same shape step 2 already uses, and what the source design specifies.
 *
 * The reference print area comes from the product resolved on entry. It is not
 * shown as a chosen product: it exists so a placement can be normalized against
 * a real print area before the seller picks anything.
 */

/**
 * Labels for the shared presets, which carry English names: translated by
 * preset id, with the preset's own name standing in for an unknown id.
 */
function toOptions(
  t: TFunction,
  presets: { id: string; name: string }[]
): PlacementOption[] {
  return [
    ...presets.map((preset) => ({
      id: preset.id,
      label: t(`creator.guided.presets.${preset.id}`, {
        defaultValue: preset.name,
      }),
    })),
    { id: CUSTOM_PLACEMENT, label: t('creator.guided.custom') },
  ];
}

/**
 * The reference product for sizing and fitting: pinned in the guided store
 * when first resolved, so a product click in step 2 does not re-fit the
 * presets against a different Print Area on the way back.
 */
function useReferenceProductId(): string | null {
  const focused = useProductSelectionStore((state) => state.focusedProductId);
  const pinned = useGuidedEntryStore((state) => state.referenceProductId);
  const pin = useGuidedEntryStore((state) => state.setReferenceProductId);
  useEffect(() => {
    if (!pinned && focused) pin(focused);
  }, [pinned, focused, pin]);
  return pinned ?? focused;
}

export function GuidedDesignStep() {
  const { t } = useTranslation();
  const { data: products } = useProducts();
  const referenceProductId = useReferenceProductId();
  const referenceProduct = products?.find((p) => p.id === referenceProductId);

  const frontOptions = useMemo(() => toOptions(t, FRONT_PRESETS), [t]);
  const backOptions = useMemo(() => toOptions(t, BACK_PRESETS), [t]);

  // Each side is fitted and sized against its own print area — the same
  // context the sync writes with, so the card never shows a size the canvas
  // state does not agree with. Without a calibrated front template (or while
  // the catalogue loads) there is no area and no card (R13).
  // Memoised: the frame builds its canvas config from these, and a fresh
  // object per render would re-run the editor's config effect every time.
  // Scaled up for the frame (guidedFrameContext): the position's mockup
  // pixels are too few to draw the motif sharp at frame size. Millimetres and
  // relative placements are unchanged, so the cards, the store and the
  // products all read the same geometry.
  const frontContext = useMemo(() => {
    const front = printContextForPosition(referenceProduct, 'front');
    return front ? guidedFrameContext(front) : null;
  }, [referenceProduct]);
  const backContext = useMemo(() => {
    const back = printContextForPosition(referenceProduct, 'back');
    return back ? guidedFrameContext(back) : frontContext;
  }, [referenceProduct, frontContext]);

  const beginCustomPlacement = useGuidedEntryStore(
    (state) => state.beginCustomPlacement
  );
  const setActivePosition = useProductSelectionStore(
    (state) => state.setActivePosition
  );
  /** Side whose handover to the editor is still in flight, if any. */
  const handoverRef = useRef<GuidedSide | null>(null);

  /**
   * Hand a side over to the canvas editor.
   *
   * The position already carries the preset's placement, so the seller starts
   * from what they saw rather than an empty frame, and then moves, scales and
   * rotates with the editor's own controls.
   */
  const startCustomPlacement = (side: GuidedSide) => {
    // One handover at a time. A second tap while the switch below is still
    // restoring would short-circuit in switchPosition, flip editingSide early
    // and remount the frame on top of a canvas the first switch still uses.
    if (handoverRef.current) return;
    const sideContext = side === 'front' ? frontContext : backContext;
    if (!sideContext) return;
    handoverRef.current = side;

    const productPositionId =
      referenceProduct?.positions?.find((p) => p.slug === side)?.id ?? null;

    // There is one canvas. If the other side is still in it, flush its edits
    // before the context switches — saveCurrentPosition reads the active
    // print context, so this has to happen ahead of setActivePosition.
    const { editingSide } = useGuidedEntryStore.getState();
    if (editingSide && editingSide !== side) {
      try {
        useCanvasStateManager.getState().saveCurrentPosition();
      } catch (error) {
        logger.warn('GuidedDesignStep', 'Could not save the open side', error);
      }
    }

    // The editor has to agree with the canvas it is given, so the active
    // context comes from the very context this side renders with — not from a
    // second lookup that could resolve differently or not at all.
    const params = guidedPrintContextParams(sideContext);
    setActivePosition(side, productPositionId, params);

    // Switch the store's own active position while the previous frame's
    // canvas is still mounted. Done after the mount instead, the editor's
    // context-sync effect runs a cross-position switch that holds the lock
    // exactly while the remount restore wants to load — the restore is
    // skipped and the new frame comes up empty. With no canvas mounted yet
    // switchPosition returns at once and the first mount loads as before.
    void useCanvasStateManager
      .getState()
      .switchPosition(side, createPrintContext(params), productPositionId)
      .catch((error) => {
        logger.warn('GuidedDesignStep', 'Position switch failed', error);
      })
      .finally(() => {
        handoverRef.current = null;
        beginCustomPlacement(side);
      });
  };

  useGuidedCanvasSync();

  const procedure = useGuidedEntryStore((state) => state.procedure);

  const { holdChoice } = useGuidedStartProcedure();
  const {
    changeProcedure,
    switchPending,
    dialogTarget,
    confirmSwitch,
    cancelSwitch,
  } = useProcedureSwitch(procedure);

  // The embroidery limits of the reference position: the per-design thread
  // ceiling and the DPI the render is sized against. The method's defaults
  // stand in until the catalogue has loaded.
  const embroideryLimits = useMemo<GuidedEmbroideryLimits>(() => {
    const method = referenceProduct?.positions
      ?.find((p) => p.slug === 'front')
      ?.availableDecorationMethods?.find((m) => m.code === 'EMBROIDERY');
    return {
      maxColors: method?.maxColors ?? DEFAULT_MAX_THREAD_COLORS,
      minDpi: method?.minDpi ?? null,
    };
  }, [referenceProduct]);

  // The machine limit, not the field: a field wider than 9 cm still stitches
  // motifs of at most 9 cm.
  const embroideryHint = t('creator.guided.embroideryHint', {
    max: embroideryLimits.maxColors,
    width: MAX_EMBROIDERY_WIDTH_MM / 10,
  });

  // Nothing but the two runs until one is chosen; the tiles then morph into
  // the compact bar and the cards glide in under it (source design: the
  // choice comes first). Layout animations read the DOM change, so the same
  // elements travel from the hero to the bar.
  const chosen = procedure !== null;
  const compact = chosen || holdChoice;

  // Inside the Shopify admin the frame is short: once a motif is on its way,
  // the run is decided and the bar folds into one line (accordion) so the
  // cards fit without scrolling. A pending embroidery analysis counts too.
  const hasMotif = useGuidedEntryStore(hasGuidedWork);
  const layout = embedLayout();
  const collapsible = chosen && hasMotif && layout.foldSecondary;
  const showAdvancedEntry = layout.hostChrome && !hasMotif;

  return (
    <main className="flex-1 overflow-y-auto bg-[var(--paper)]">
      <LayoutGroup>
        <motion.div
          layout
          transition={CHOICE_TRANSITION}
          className={cn(
            'dc-step flex flex-col gap-[18px] px-5 pb-14',
            compact ? 'pt-[34px]' : 'pt-[9vh]'
          )}
        >
          {holdChoice ? (
            <ProcedurePending />
          ) : (
            <GuidedProcedureChoice
              value={procedure}
              large={!chosen}
              collapsible={collapsible}
              onChange={changeProcedure}
              embroideryHint={embroideryHint}
            />
          )}

          <PreselectedProductNotice
            procedure={procedure}
            onChangeProcedure={changeProcedure}
          />

          {chosen && products && !frontContext && (
            <p className="text-[13px] text-[var(--ink-2)]" role="status">
              {t('creator.guided.referenceUnavailable')}
            </p>
          )}

          <AnimatePresence mode="wait" initial={false}>
            {chosen && frontContext && backContext && (
              <motion.div
                key={procedure}
                layout
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.55, ease: 'easeOut' }}
              >
                {procedure === 'embroidery' ? (
                  <div className="grid items-start gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(600px,1fr))]">
                    <GuidedMotifCard
                      side="front"
                      title={t('creator.guided.embroideryTitle')}
                      subtitle={t('creator.guided.embroiderySubtitle', {
                        max: embroideryLimits.maxColors,
                      })}
                      dropTitle={t('creator.guided.embroideryDropTitle')}
                      emptyHint={t('creator.guided.embroideryEmptyHint')}
                      placements={[]}
                      printContext={frontContext}
                      embroidery={embroideryLimits}
                    />
                  </div>
                ) : (
                  // 350 px is the narrowest card that still holds the 280 px
                  // frame, so front and back sit side by side inside a
                  // shop's iframe too; the card stacks its own contents
                  // below 600 px (see GuidedMotifCard).
                  <div className="dc-panel-grid grid items-start gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(350px,1fr))]">
                    <GuidedMotifCard
                      side="front"
                      title={t('creator.guided.frontTitle')}
                      subtitle={t('creator.guided.frontSubtitle')}
                      dropTitle={t('creator.guided.frontDropTitle')}
                      emptyHint={t('creator.guided.frontEmptyHint')}
                      placements={frontOptions}
                      printContext={frontContext}
                      onCustomPlacement={() => startCustomPlacement('front')}
                    />
                    <GuidedMotifCard
                      side="back"
                      title={t('creator.guided.backTitle')}
                      subtitle={t('creator.guided.backSubtitle')}
                      dropTitle={t('creator.guided.backDropTitle')}
                      emptyHint={t('creator.guided.backEmptyHint')}
                      placements={backOptions}
                      printContext={backContext}
                      onCustomPlacement={() => startCustomPlacement('back')}
                    />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Host-framed (Shopify admin): the other modes stay one quiet
              click away, and only before a motif is on its way. */}
          {showAdvancedEntry && <AdvancedEntryLink />}
        </motion.div>
      </LayoutGroup>

      <ConfirmDialog
        open={switchPending}
        onOpenChange={(next) => {
          if (!next) cancelSwitch();
        }}
        title={t(
          dialogTarget === 'embroidery'
            ? 'creator.guided.switchToEmbroideryTitle'
            : 'creator.guided.switchToPrintTitle'
        )}
        description={t(
          dialogTarget === 'embroidery'
            ? 'creator.guided.switchToEmbroideryBody'
            : 'creator.guided.switchToPrintBody'
        )}
        cancelLabel={t('common.cancel')}
        confirmLabel={t('creator.guided.switchConfirm')}
        destructive
        onConfirm={confirmSwitch}
      />
    </main>
  );
}

/**
 * Tells the seller about a product that came in with the entry URL
 * (`?product=`, e.g. the marketplace's "Design this product" button) and is
 * already ticked in step 2.
 *
 * When that product cannot be decorated with the chosen run, nothing is
 * changed on the seller's behalf: both ways out are offered as actions. A
 * deselected product leaves the motif alone — only the selection is dropped.
 */
function PreselectedProductNotice({
  procedure,
  onChangeProcedure,
}: {
  procedure: GuidedProcedure | null;
  onChangeProcedure: (procedure: GuidedProcedure) => void;
}) {
  const { t } = useTranslation();
  // The product CreatorPage actually preselected, from `?product=` or `?SKU=`.
  const productId = useGuidedEntryStore(
    (state) => state.preselection?.productId ?? null
  );
  const { data: products } = useProducts();
  const selected = useProductSelectionStore((state) =>
    productId ? state.selections[productId]?.isActive === true : false
  );
  const toggleProduct = useProductSelectionStore(
    (state) => state.toggleProduct
  );

  const product = productId ? products?.find((p) => p.id === productId) : null;
  if (!product || !selected) return null;

  const mismatch =
    procedure !== null &&
    productSupportsProcedure(product, procedure) === false;
  const alternative = procedure ? otherProcedure(procedure) : null;

  return (
    <div className="flex flex-col gap-2" data-testid="preselected-product">
      {/* The "preselected" hint only holds while the product works with the
          chosen procedure; on a mismatch the warning below replaces it. */}
      {!mismatch && (
        <p
          role="status"
          className="rounded-card bg-[var(--paper-2)] px-4 py-3 text-[13px] font-medium text-[var(--success)]"
        >
          {t('creator.guided.productPreselected', { name: product.name })}
        </p>
      )}
      {mismatch && alternative && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-card bg-[var(--paper-2)] px-4 py-3 text-[13px] text-[var(--warning)]"
        >
          <span className="font-medium">
            {t(
              alternative === 'print'
                ? 'creator.guided.productOnlyPrint'
                : 'creator.guided.productOnlyEmbroidery',
              { name: product.name }
            )}
          </span>
          <button
            type="button"
            onClick={() => onChangeProcedure(alternative)}
            className="cursor-pointer font-semibold text-[var(--cobalt-500)]"
          >
            {t(
              alternative === 'print'
                ? 'creator.guided.usePrint'
                : 'creator.guided.useEmbroidery'
            )}
          </button>
          <button
            type="button"
            onClick={() => toggleProduct(product.id, false)}
            className="cursor-pointer font-semibold text-[var(--cobalt-500)]"
          >
            {t('creator.guided.deselectProduct')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Stands in for the procedure bar while the embedded start is still being
 * decided: same panel, roughly the compact bar's height, nothing to click.
 */
function ProcedurePending() {
  const { t } = useTranslation();
  return (
    <section
      aria-label={t('creator.guided.procedure')}
      aria-busy="true"
      data-testid="procedure-pending"
      className="dc-panel flex min-h-[140px] flex-col gap-3 rounded-card bg-[var(--paper-2)] p-5"
    >
      <span className="sr-only">{t('creator.guided.procedureLoading')}</span>
      <span
        aria-hidden="true"
        className="h-4 w-24 rounded-control bg-[var(--paper)] motion-safe:animate-pulse"
      />
      <span
        aria-hidden="true"
        className="h-[68px] rounded-card bg-[var(--paper)] motion-safe:animate-pulse"
      />
    </section>
  );
}

/**
 * Keeps canvas state in step with what the seller authored here.
 *
 * The step guards and the save path both read canvasStateManager, so the
 * guided step cannot hold its motifs as intent only — it would be blocked by
 * its own design, and a save would drop it. Syncing on every change rather
 * than on leaving keeps that state true at all times, and keeps the navigation
 * components unaware of this flow.
 *
 * A side the reference product cannot carry is reported once, not swallowed.
 */
function useGuidedCanvasSync(): void {
  const { data: products } = useProducts();
  // Sides this surface has written. Clearing is limited to them, so state that
  // arrived from a loaded design is never mistaken for a removal.
  const written = useRef<Set<string>>(new Set());
  // Structural skips already shown, keyed `position:reason`.
  const reported = useRef<Set<string>>(new Set());
  const front = useGuidedEntryStore((s) => s.front);
  const back = useGuidedEntryStore((s) => s.back);
  const referenceProductId = useReferenceProductId();

  useEffect(() => {
    const sides = [
      { position: 'front', state: front },
      { position: 'back', state: back },
    ];

    const manager = useCanvasStateManager.getState();

    // A motif in the guided store is one this surface owns: uploaded here, or
    // hydrated from a guided design that loadDesign reset the store for first
    // — including a custom side, which never passes through the write below.
    // Recording it here keeps ownership across a remount of this step.
    for (const { position, state } of sides) {
      if (state.motif) written.current.add(position);
    }

    // Drop a side the seller removed here, so a deleted motif does not linger
    // as a position that still counts as a design. Only sides this surface
    // wrote are eligible: an empty guided store on mount means "nothing
    // authored yet", never "the loaded design should be emptied".
    for (const { position, state } of sides) {
      if (!state.motif && written.current.has(position)) {
        written.current.delete(position);
        if (manager.masterPositions[position]) {
          manager.clearPosition(position);
          // The store only marks the design dirty on canvas events; a
          // headless clear has to do it itself or the next step change
          // skips the save.
          useDesignStore.getState().setHasUnsavedChanges(true);
        }
      }
    }

    if (!sides.some((s) => s.state.motif)) return;

    // A side placed freely is written by the canvas; only its authoring
    // intent is ours to record, so it re-opens as "Individuell".
    for (const { position, state } of sides) {
      if (state.motif && state.placement === CUSTOM_PLACEMENT) {
        manager.setPositionAuthoring(position, {
          placementPresetId: CUSTOM_PLACEMENT,
          designWidthMm: null,
        });
      }
    }

    const referenceProduct = products?.find((p) => p.id === referenceProductId);
    const { entries, skipped } = planGuidedCommit(sides, referenceProduct);

    for (const entry of entries) {
      try {
        manager.writePositionFromPlacement({
          position: entry.position,
          placementMm: entry.placementMm,
          designCropUrl: entry.designCropUrl,
          source: entry.source,
          printContext: entry.printContext,
          productPositionId: entry.productPositionId,
          placementPresetId: entry.placementPresetId,
          designWidthMm: entry.designWidthMm,
          objectProps: entry.objectProps,
        });
        written.current.add(entry.position);
        useDesignStore.getState().setHasUnsavedChanges(true);
      } catch (error) {
        // A rejected write means a degenerate placement — say so rather than
        // letting the seller believe the motif came along.
        logger.warn(
          'GuidedDesignStep',
          `Write failed for ${entry.position}`,
          error
        );
        toast.error(guidedCommitSkipMessage(entry.position, 'no-placement'));
      }
    }

    // This effect re-runs on every side, size or catalogue change; a
    // structural mismatch is reported once per position until it changes.
    const seen = new Set<string>();
    for (const skip of skipped) {
      // 'no-placement' is the normal state while a custom placement is still
      // being drawn; only structural mismatches are worth interrupting for.
      if (skip.reason === 'no-placement') continue;
      const key = `${skip.position}:${skip.reason}`;
      seen.add(key);
      if (reported.current.has(key)) continue;
      reported.current.add(key);
      toast.error(guidedCommitSkipMessage(skip.position, skip.reason));
    }
    for (const key of reported.current) {
      if (!seen.has(key)) reported.current.delete(key);
    }
  }, [front, back, products, referenceProductId]);
}
